import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import { useDispatch } from "react-redux"
import useTenant from "hooks/useTenant"
import { setTenant } from "store/slices/tenantSlice"
import { useTranslation } from "react-i18next"

/**
 * Dropdown component that allows switching between available tenants.
 *
 * Reads the current tenant from TenantContext and writes back via both the
 * context (`setTenant`) and the Redux store (`setTenant` action) so that
 * all parts of the app stay in sync.
 *
 * @example
 * <TenantSwitcher tenants={[{ id: 'acme', name: 'Acme Corp' }]} />
 */
const TenantSwitcher = ({ tenants }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { tenantId, tenantName, setTenant: setContextTenant } = useTenant()
  const [isOpen, setIsOpen] = useState(false)

  const toggle = () => setIsOpen(prev => !prev)

  const handleSwitch = tenant => {
    if (tenant.id === tenantId) return
    setContextTenant({
      tenantId: tenant.id,
      tenantName: tenant.name,
      tenantSettings: tenant.settings || {},
    })
    dispatch(
      setTenant({
        tenantId: tenant.id,
        tenantName: tenant.name,
        tenantSettings: tenant.settings || {},
      })
    )
  }

  const displayName = tenantName || tenantId || t("Select Tenant")

  if (!tenants || tenants.length === 0) return null

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} className="d-inline-block ms-1">
      <DropdownToggle
        tag="button"
        className="btn header-item d-flex align-items-center gap-1"
        title={t("Switch Tenant")}
      >
        <i className="mdi mdi-domain font-size-18"></i>
        <span className="d-none d-xl-inline-block ms-1 me-1 fs-6">
          {displayName}
        </span>
        <i className="mdi mdi-chevron-down d-none d-xl-inline-block"></i>
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-end">
        <div className="dropdown-header text-muted small px-3 py-1">
          {t("Switch Tenant")}
        </div>
        <DropdownItem divider />
        {tenants.map(tenant => (
          <DropdownItem
            key={tenant.id}
            onClick={() => handleSwitch(tenant)}
            active={tenant.id === tenantId}
          >
            <i className="mdi mdi-domain me-2"></i>
            {tenant.name || tenant.id}
            {tenant.id === tenantId && (
              <i className="mdi mdi-check ms-2 text-success"></i>
            )}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  )
}

TenantSwitcher.propTypes = {
  tenants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      settings: PropTypes.object,
    })
  ),
}

TenantSwitcher.defaultProps = {
  tenants: [],
}

export default TenantSwitcher
